import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Users, FileText, ChatsCircle, Target, SignOut } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmployerDashboard = () => {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, candidatesRes] = await Promise.all([
        axios.get(`${API}/employer/dashboard-stats`),
        axios.get(`${API}/employer/candidates`)
      ]);
      setStats(statsRes.data);
      setCandidates(candidatesRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-xl font-medium">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-white border-b border-black/5 sticky top-0 z-10">
        <div className="px-6 md:px-8 lg:px-12 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black tracking-tighter" data-testid="dashboard-title">TalentoX</h1>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-medium" data-testid="user-name">{user?.full_name}</p>
              <p className="text-xs text-[#A1A1AA]" data-testid="user-role">Employer</p>
            </div>
            <button
              onClick={logout}
              className="p-2 hover:bg-black/5 transition-colors rounded-none"
              data-testid="logout-button"
            >
              <SignOut size={20} />
            </button>
          </div>
        </div>
      </header>

      <div className="px-6 md:px-8 lg:px-12 py-8">
        {/* Welcome Section */}
        <div className="mb-12">
          <h2 className="text-4xl sm:text-5xl font-black tracking-tighter leading-none mb-4">
            Employer Dashboard
          </h2>
          <p className="text-base font-light text-[#52525B] max-w-2xl">
            Access AI-powered candidate insights and streamline your hiring pipeline.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6 mb-8">
          <div className="bg-white border border-black/5 p-6" data-testid="total-candidates-card">
            <div className="flex items-center gap-3 mb-4">
              <Users size={24} className="text-[#0033FF]" />
              <h3 className="text-xs font-semibold uppercase tracking-wider">Total Candidates</h3>
            </div>
            <div className="text-4xl font-black tracking-tighter" data-testid="total-candidates">
              {stats?.total_candidates || 0}
            </div>
          </div>

          <div className="bg-white border border-black/5 p-6" data-testid="total-analyses-card">
            <div className="flex items-center gap-3 mb-4">
              <FileText size={24} className="text-[#0033FF]" />
              <h3 className="text-xs font-semibold uppercase tracking-wider">Analyses</h3>
            </div>
            <div className="text-4xl font-black tracking-tighter" data-testid="total-analyses">
              {stats?.total_analyses || 0}
            </div>
          </div>

          <div className="bg-white border border-black/5 p-6" data-testid="total-interviews-card">
            <div className="flex items-center gap-3 mb-4">
              <ChatsCircle size={24} className="text-[#0033FF]" />
              <h3 className="text-xs font-semibold uppercase tracking-wider">Interviews</h3>
            </div>
            <div className="text-4xl font-black tracking-tighter" data-testid="total-interviews">
              {stats?.total_interviews || 0}
            </div>
          </div>

          <div className="bg-white border border-black/5 p-6" data-testid="high-readiness-card">
            <div className="flex items-center gap-3 mb-4">
              <Target size={24} className="text-[#0033FF]" />
              <h3 className="text-xs font-semibold uppercase tracking-wider">High Readiness</h3>
            </div>
            <div className="text-4xl font-black tracking-tighter" data-testid="high-readiness">
              {stats?.high_readiness_count || 0}
            </div>
          </div>
        </div>

        {/* Candidates Table */}
        <div className="bg-white border border-black/5" data-testid="candidates-table">
          <div className="p-6 border-b border-black/5">
            <h3 className="text-xl font-semibold">Candidate Pipeline</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#F9F9FB] border-b border-black/5">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Readiness Score</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Interviews</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-black/5">
                {candidates.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center text-[#A1A1AA] font-light">
                      No candidates yet
                    </td>
                  </tr>
                ) : (
                  candidates.map((candidate, idx) => (
                    <tr key={candidate.user_id} className="hover:bg-[#F9F9FB] transition-colors" data-testid={`candidate-row-${idx}`}>
                      <td className="px-6 py-4 font-medium" data-testid={`candidate-name-${idx}`}>
                        {candidate.full_name}
                      </td>
                      <td className="px-6 py-4 font-light text-[#52525B]" data-testid={`candidate-email-${idx}`}>
                        {candidate.email}
                      </td>
                      <td className="px-6 py-4" data-testid={`candidate-score-${idx}`}>
                        {candidate.latest_analysis ? (
                          <span className="text-2xl font-bold">
                            {candidate.latest_analysis.job_readiness_score}
                          </span>
                        ) : (
                          <span className="text-[#A1A1AA] font-light">N/A</span>
                        )}
                      </td>
                      <td className="px-6 py-4 font-light" data-testid={`candidate-sessions-${idx}`}>
                        {candidate.total_sessions}
                      </td>
                      <td className="px-6 py-4" data-testid={`candidate-status-${idx}`}>
                        {candidate.latest_analysis ? (
                          candidate.latest_analysis.job_readiness_score >= 80 ? (
                            <span className="px-3 py-1 bg-[#0033FF] text-white text-xs font-semibold uppercase tracking-wider rounded-none">
                              Ready
                            </span>
                          ) : candidate.latest_analysis.job_readiness_score >= 60 ? (
                            <span className="px-3 py-1 bg-[#FFD600] text-[#0A0A0A] text-xs font-semibold uppercase tracking-wider rounded-none">
                              Training
                            </span>
                          ) : (
                            <span className="px-3 py-1 bg-[#F1F1F4] text-[#52525B] text-xs font-semibold uppercase tracking-wider rounded-none">
                              Early
                            </span>
                          )
                        ) : (
                          <span className="px-3 py-1 bg-[#F1F1F4] text-[#52525B] text-xs font-semibold uppercase tracking-wider rounded-none">
                            New
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployerDashboard;
